(function () {
  if (customElements.get('bt-video-row')) return;

  const VIDEO_URL_KEYS = ['url', 'video_url', 'videoUrl', 'src', 'source', 'embed_url', 'embedUrl'];
  const VIDEO_LIST_KEYS = ['videos', 'video_urls', 'videoUrls', 'urls', 'sources', 'items', 'data', 'results'];
  const POSTER_URL_KEYS = ['poster_url', 'posterUrl', 'thumbnail_url', 'thumbnailUrl'];

  class BtVideoRow extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-bt-video-row-section]');
      this.grid = this.querySelector('[data-bt-video-grid]');
      this.template = this.querySelector('[data-bt-video-template]');
      this.videos = [];

      if (!this.grid || !this.template) return;

      this.loadVideoUrl();
    }

    loadVideoUrl() {
      const source = this.dataset.src;

      if (!source) {
        this.removeSection();
        return;
      }

      fetch(source, { headers: { Accept: 'application/json, text/plain;q=0.9' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Video URL unavailable.');

          const contentType = response.headers.get('content-type') || '';
          return contentType.includes('application/json') ? response.json() : response.text();
        })
        .then(this.handleVideoPayload.bind(this))
        .catch(this.removeSection.bind(this));
    }

    handleVideoPayload(payload) {
      const videos = this.getPayloadVideos(payload);

      if (!videos.length) {
        this.removeSection();
        return;
      }

      this.videos = videos;
  this.renderVideos();
      if (this.section) this.section.hidden = false;
    }

    getPayloadVideos(payload) {
      const videos = this.collectPayloadVideos(payload);
      const seenUrls = new Set();

      return videos.filter(function (video) {
        if (!video.url || seenUrls.has(video.url)) return false;
        seenUrls.add(video.url);
        return true;
      });
    }

    collectPayloadVideos(payload, inheritedPosterUrl = '') {
      if (typeof payload === 'string') {
        const url = payload.trim();
        return url ? [{ url, posterUrl: inheritedPosterUrl }] : [];
      }

      if (!payload || typeof payload !== 'object') return [];

      if (Array.isArray(payload)) {
        return payload.flatMap((item) => this.collectPayloadVideos(item, inheritedPosterUrl));
      }

      const posterUrl = this.getObjectPoster(payload) || inheritedPosterUrl;
      const videos = [];

      for (const key of VIDEO_URL_KEYS) {
        videos.push(...this.collectVideoValue(payload[key], posterUrl));
      }

      if (payload.video) {
        videos.push(...this.collectVideoValue(payload.video, posterUrl));
      }

      for (const key of VIDEO_LIST_KEYS) {
        videos.push(...this.collectVideoValue(payload[key], posterUrl));
      }

      return videos;
    }

    collectVideoValue(value, posterUrl) {
      if (typeof value === 'string') {
        const url = value.trim();
        return url ? [{ url, posterUrl }] : [];
      }

      if (!value || typeof value !== 'object') return [];

      const videos = this.collectPayloadVideos(value, posterUrl);
      if (videos.length) return videos;

      return this.collectMappedVideos(value, posterUrl);
    }

    collectMappedVideos(payload, posterUrl) {
      return Object.keys(payload).flatMap((key) => {
        if (VIDEO_URL_KEYS.includes(key) || VIDEO_LIST_KEYS.includes(key) || POSTER_URL_KEYS.includes(key)) return [];

        return this.collectVideoValue(payload[key], posterUrl);
      });
    }

    getObjectPoster(payload) {
      for (const key of POSTER_URL_KEYS) {
        if (typeof payload[key] === 'string' && payload[key].trim()) return payload[key].trim();
      }

      return '';
    }

    getVideoPoster(video) {
      if (video.posterUrl) return video.posterUrl;

      const youtubeId = this.getYouTubeId(video.url);
      return youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : '';
    }

    applyPoster(button, video) {
      const posterUrl = this.getVideoPoster(video);

      if (posterUrl) {
        button.style.setProperty('--bt-video-row-poster', `url(${JSON.stringify(posterUrl)})`);
        return;
      }

      button.style.removeProperty('--bt-video-row-poster');
    }

    renderVideos() {
      const cards = document.createDocumentFragment();

      this.videos.forEach((video) => {
        const card = this.template.content.cloneNode(true);
        const button = card.querySelector('[data-bt-video-play]');
        const stage = card.querySelector('[data-bt-video-stage]');

        if (!button || !stage) return;

        button.disabled = false;
        button.addEventListener('click', () => this.play(video, stage));
        this.applyPoster(button, video);
        cards.appendChild(card);
      });

      this.grid.replaceChildren(cards);
    }

    play(video, stage) {
      if (!video || stage.dataset.loaded === 'true') return;

      window.pauseAllMedia?.();

      const mediaElement = this.createMediaElement(video.url);
      if (!mediaElement) return;

      stage.dataset.loaded = 'true';
      stage.replaceChildren(mediaElement);
      mediaElement.focus();

      if (mediaElement.nodeName === 'VIDEO') {
        mediaElement.play().catch(function () {});
      }
    }

    createMediaElement(rawUrl) {
      let parsedUrl;

      try {
        parsedUrl = new URL(rawUrl, window.location.origin);
      } catch (error) {
        return null;
      }

      const youtubeId = this.getYouTubeId(parsedUrl.href);
      if (youtubeId) {
        return this.createIframe(`https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&autoplay=1`);
      }

      const vimeoId = this.getVimeoId(parsedUrl.href);
      if (vimeoId) {
        return this.createIframe(`https://player.vimeo.com/video/${vimeoId}?autoplay=1`);
      }

      if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(parsedUrl.pathname)) {
        const video = document.createElement('video');
        video.src = parsedUrl.href;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        video.setAttribute('tabindex', '-1');
        return video;
      }

      parsedUrl.searchParams.set('autoplay', '1');
      return this.createIframe(parsedUrl.href);
    }

    createIframe(src) {
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.title = this.dataset.videoTitle || this.dataset.playLabel || 'Video';
      iframe.setAttribute('tabindex', '-1');
      return iframe;
    }

    getYouTubeId(url) {
      const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
      return match ? match[1] : '';
    }

    getVimeoId(url) {
      const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      return match ? match[1] : '';
    }

    removeSection() {
      if (this.section) {
        this.section.remove();
        return;
      }

      this.remove();
    }
  }

  customElements.define('bt-video-row', BtVideoRow);
})();